import React, { useState, useEffect } from 'react';
import DeliveryChallanPDF from './DeliveryChallanPDF';
import { BlobProvider, type BlobProviderParams } from '@react-pdf/renderer';
import { DeliveryChallan } from '../types/billing';
import { formatDate } from '../utils/formatters';

interface DeliveryChallanManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  challan: DeliveryChallan | null;
  onUpdateChallan: (challan: DeliveryChallan) => Promise<void>;
  onMarkAsReturned: (challanId: string, returnCondition?: string, notes?: string) => Promise<void>;
  onMarkAsOverdue: (challanId: string) => Promise<void>;
  onMarkAsLost: (challanId: string, notes?: string) => Promise<void>;
  onDeleteChallan: (challanId: string) => Promise<void>;
}

const DeliveryChallanManagementModal: React.FC<DeliveryChallanManagementModalProps> = ({
  isOpen,
  onClose,
  challan,
  onUpdateChallan,
  onMarkAsReturned,
  onMarkAsOverdue,
  onMarkAsLost,
  onDeleteChallan
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'actions' | 'lost'>('details');
  const [returnCondition, setReturnCondition] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [lostNotes, setLostNotes] = useState('');

  if (!isOpen || !challan) return null;

  const handleMarkAsReturned = async () => {
    if (!challan.id) return;

    setIsLoading(true);
    try {
      await onMarkAsReturned(challan.id, returnCondition, returnNotes);
      setReturnCondition('');
      setReturnNotes('');
    } catch (error) {
      console.error('Error marking challan as returned:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsOverdue = async () => {
    if (!challan.id) return;

    if (window.confirm('Are you sure you want to mark this challan as overdue?')) {
      setIsLoading(true);
      try {
        await onMarkAsOverdue(challan.id);
      } catch (error) {
        console.error('Error marking challan as overdue:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMarkAsLost = async () => {
    if (!challan.id) return;

    if (window.confirm('Are you sure you want to mark this challan as lost? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        await onMarkAsLost(challan.id, lostNotes);
        setLostNotes('');
      } catch (error) {
        console.error('Error marking challan as lost:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteChallan = async () => {
    if (!challan.id) return;

    if (window.confirm('Are you sure you want to delete this delivery challan? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        await onDeleteChallan(challan.id);
        onClose();
      } catch (error) {
        console.error('Error deleting challan:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getStatusColor = (status: DeliveryChallan['status']) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Returned': return 'bg-blue-100 text-blue-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      case 'Lost': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = () => {
    if (challan.status !== 'Active' || !challan.expectedReturnDate) return false;
    return new Date(challan.expectedReturnDate) < new Date();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Delivery Challan Management - {challan.challanNumber}
            </h3>
            <div className="flex items-center gap-3">
              <BlobProvider document={<DeliveryChallanPDF challan={challan} />}>
                {({ url, loading, error }: BlobProviderParams) => (
                  <a
                    href={url ?? undefined}
                    download={`DeliveryChallan_${challan.challanNumber}.pdf`}
                    onClick={(e) => {
                      if (!url || loading || error) e.preventDefault();
                    }}
                    className={`px-3 py-2 rounded text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    aria-disabled={loading || !!error}
                  >
                    {loading ? 'Preparing PDF…' : error ? 'Failed to load PDF' : 'Download PDF'}
                  </a>
                )}
              </BlobProvider>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Challan Details
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'actions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Actions
              </button>
            </nav>
          </div>

          {/* Content */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Challan Information</h4>
                  <div className="space-y-2">
                    <div><span className="font-medium">Challan Number:</span> {challan.challanNumber}</div>
                    <div><span className="font-medium">Date:</span> {formatDate(challan.challanDate)}</div>
                    <div><span className="font-medium">Status:</span>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(challan.status)}`}>
                        {challan.status}
                      </span>
                    </div>
                    {challan.expectedReturnDate && (
                      <div><span className="font-medium">Expected Return:</span> {formatDate(challan.expectedReturnDate)}</div>
                    )}
                    {challan.actualReturnDate && (
                      <div><span className="font-medium">Actual Return:</span> {formatDate(challan.actualReturnDate)}</div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Personnel Information</h4>
                  <div className="space-y-2">
                    <div><span className="font-medium">Employee:</span> {challan.employeeName}</div>
                    {challan.employeeId && <div><span className="font-medium">Employee ID:</span> {challan.employeeId}</div>}
                    <div><span className="font-medium">Purpose:</span> {challan.purpose}</div>
                    <div><span className="font-medium">Issued By:</span> {challan.issuedBy}</div>
                    <div><span className="font-medium">Received By:</span> {challan.receivedBy}</div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="bg-white border rounded-lg">
                <h4 className="font-medium text-gray-900 p-4 border-b">Items/Instruments</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          S.N.
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {challan.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {item.sn}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {item.description}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {item.qty}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {item.remark || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Information */}
              {(challan.notes || challan.returnCondition) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                  {challan.notes && (
                    <div className="mb-3">
                      <span className="font-medium">Notes:</span>
                      <p className="mt-1 text-sm text-gray-700">{challan.notes}</p>
                    </div>
                  )}
                  {challan.returnCondition && (
                    <div>
                      <span className="font-medium">Return Condition:</span>
                      <p className="mt-1 text-sm text-gray-700">{challan.returnCondition}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              {/* Status Actions */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-4">Status Actions</h4>

                {challan.status === 'Active' && (
                  <div className="space-y-4">
                    {isOverdue() && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h5 className="text-sm font-medium text-red-800">
                              This challan is overdue!
                            </h5>
                            <p className="text-sm text-red-700 mt-1">
                              Expected return date: {formatDate(challan.expectedReturnDate!)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleMarkAsReturned}
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Mark as Returned
                    </button>

                    {isOverdue() && (
                      <button
                        onClick={handleMarkAsOverdue}
                        disabled={isLoading}
                        className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        Mark as Overdue
                      </button>
                    )}

                    <button
                      onClick={() => setActiveTab('lost')}
                      className="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                    >
                      Report as Lost
                    </button>
                  </div>
                )}

                {challan.status === 'Returned' && (
                  <div className="text-center text-green-600">
                    <p className="text-lg font-medium">✓ This challan has been returned</p>
                    {challan.actualReturnDate && (
                      <p className="text-sm">Returned on: {formatDate(challan.actualReturnDate)}</p>
                    )}
                  </div>
                )}

                {challan.status === 'Overdue' && (
                  <div className="text-center text-red-600">
                    <p className="text-lg font-medium">⚠ This challan is overdue</p>
                    {challan.expectedReturnDate && (
                      <p className="text-sm">Expected return: {formatDate(challan.expectedReturnDate)}</p>
                    )}
                  </div>
                )}

                {challan.status === 'Lost' && (
                  <div className="text-center text-gray-600">
                    <p className="text-lg font-medium">✗ This challan has been reported as lost</p>
                  </div>
                )}
              </div>

              {/* Lost Form */}
              {challan.status === 'Active' && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Report as Lost</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Loss Details
                      </label>
                      <textarea
                        value={lostNotes}
                        onChange={(e) => setLostNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Describe circumstances of loss, last known location, etc."
                        required
                      />
                    </div>
                    <button
                      onClick={handleMarkAsLost}
                      disabled={isLoading || !lostNotes.trim()}
                      className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      Report as Lost
                    </button>
                  </div>
                </div>
              )}

              {/* Danger Zone */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-3">Danger Zone</h4>
                <p className="text-sm text-red-700 mb-3">
                  These actions are permanent and cannot be undone.
                </p>
                <button
                  onClick={handleDeleteChallan}
                  disabled={isLoading}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Delete Challan
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryChallanManagementModal;
